import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight, MessageSquareOff, Search, Users, X } from "lucide-react";
import { useState } from "react";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import {
  LeadSourceBadge,
  LeadStatusBadge,
  LeadTemperatureBadge,
} from "@/components/crm/LeadControls";
import { NewLeadDialog } from "@/components/crm/NewLeadDialog";
import { PeriodPicker } from "@/components/crm/PeriodPicker";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card, CardFooter } from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Hint } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import {
  formatPhone,
  relativeTime,
  SOURCE_GROUP,
  SOURCE_GROUP_LABEL,
  SOURCE_LABEL,
} from "@/lib/crm";
import { usePeriod } from "@/lib/period";
import { cn } from "@/lib/utils";
import { LEAD_STATUS_LABEL, LEAD_TEMPERATURE_LABEL } from "@rn/contracts";

type LeadRow = {
  id: string;
  status: keyof typeof LEAD_STATUS_LABEL;
  temperature: keyof typeof LEAD_TEMPERATURE_LABEL;
  leadScore: number;
  source: string | null;
  createdAt: string;
  firstResponseAt: string | null;
  contact: { fullName: string | null; phoneNormalized: string | null; email: string | null };
  owner: { fullName: string | null } | null;
  campaignName: string | null;
};

type LeadList = {
  data: LeadRow[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
};

export const Route = createFileRoute("/_authenticated/leads/")({
  component: LeadsPage,
});

function LeadsPage() {
  const { period, query, key } = usePeriod();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  // A página vale para o período em que foi escolhida: trocar o período volta
  // para a primeira, porque a página 4 de "este mês" pode nem existir em
  // "ontem" e a tabela abriria vazia. Guardar o período junto da página, em
  // vez de zerar num efeito, evita buscar a página velha no período novo antes.
  const [pagina, setPagina] = useState({ key, n: 1 });
  const page = pagina.key === key ? pagina.n : 1;
  const setPage = (n: number) => setPagina({ key, n });
  const [onlyUnanswered, setOnlyUnanswered] = useState(false);
  // "grupo:pago" filtra por todas as origens pagas de uma vez — é a pergunta
  // que se faz de verdade ("quem veio de anúncio?"), e escolher três origens
  // uma a uma para respondê-la ninguém faz.
  const [origem, setOrigem] = useState("");

  // Sem debounce, cada tecla vira uma requisição com ILIKE em duas colunas.
  const debouncedSearch = useDebouncedValue(search, 300);

  const origens = origem.startsWith("grupo:")
    ? Object.keys(SOURCE_GROUP).filter((s) => SOURCE_GROUP[s] === origem.slice(6))
    : origem
      ? [origem]
      : undefined;

  // "Todo o período" vai sem filtro de data, e não como `preset=all`: é o
  // contrato da listagem (sem período, todos), e esta é a tela de trabalho —
  // lead nenhum pode sumir dela por ter entrado antes do dia em que o servidor
  // começa a contar "todo o período".
  const filtroDePeriodo = period.preset === "all" ? {} : query;

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["leads", { page, search: debouncedSearch, onlyUnanswered, origem, periodo: key }],
    queryFn: () =>
      api.get<LeadList>("/leads", {
        page,
        pageSize: 25,
        search: debouncedSearch || undefined,
        unanswered: onlyUnanswered || undefined,
        source: origens,
        ...filtroDePeriodo,
      }),
    // Mantém a página anterior visível durante a busca: sem isso a tabela
    // pisca em branco a cada tecla.
    placeholderData: keepPreviousData,
  });

  const rows = data?.data ?? [];
  const pagination = data?.pagination;
  const filtrando = onlyUnanswered || origem !== "";

  const abrir = (leadId: string) => navigate({ to: "/leads/$leadId", params: { leadId } });

  return (
    <>
      <PageHeader
        title="Leads"
        description={
          pagination
            ? `${pagination.total.toLocaleString("pt-BR")} ${period.preset === "all" ? "no total" : "no período"}`
            : "Cada captação, uma linha"
        }
        action={
          <>
            <PeriodPicker />
            <NewLeadDialog />
          </>
        }
      >
        <div className="relative min-w-[12rem] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Buscar por nome, telefone ou e-mail"
            aria-label="Buscar leads"
            className="pl-8 pr-8"
          />
          {search && (
            <button
              type="button"
              aria-label="Limpar busca"
              onClick={() => {
                setSearch("");
                setPage(1);
              }}
              className="absolute right-1.5 top-1/2 inline-flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="size-3.5" />
            </button>
          )}
        </div>
        <Button
          variant={onlyUnanswered ? "default" : "outline"}
          aria-pressed={onlyUnanswered}
          onClick={() => {
            setOnlyUnanswered((v) => !v);
            setPage(1);
          }}
        >
          <MessageSquareOff />
          Sem atendimento
        </Button>
        <Select
          value={origem}
          onChange={(e) => {
            setOrigem(e.target.value);
            setPage(1);
          }}
          aria-label="Origem"
          className="w-full sm:w-52"
        >
          <option value="">Todas as origens</option>
          <optgroup label="Agrupado">
            {Object.entries(SOURCE_GROUP_LABEL).map(([valor, rotulo]) => (
              <option key={valor} value={`grupo:${valor}`}>
                {rotulo}
              </option>
            ))}
          </optgroup>
          <optgroup label="Por origem">
            {Object.entries(SOURCE_LABEL).map(([valor, rotulo]) => (
              <option key={valor} value={valor}>
                {rotulo}
              </option>
            ))}
          </optgroup>
        </Select>
        {isFetching && !isLoading && (
          <span className="text-xs text-muted-foreground" aria-live="polite">
            atualizando…
          </span>
        )}
      </PageHeader>

      <PageBody>
        <Card className="overflow-hidden">
          {isLoading ? (
            <LinhasCarregando />
          ) : rows.length === 0 ? (
            debouncedSearch ? (
              <EmptyState
                icon={Search}
                title="Nada encontrado para essa busca."
                description={`Nenhum lead combina com "${debouncedSearch}". Tente parte do nome ou o telefone só com números.`}
                action={
                  <Button variant="outline" size="sm" onClick={() => setSearch("")}>
                    Limpar busca
                  </Button>
                }
              />
            ) : filtrando ? (
              <EmptyState
                icon={Users}
                title="Nenhum lead com esses filtros."
                description="Há leads no período, mas nenhum sem atendimento ou dessa origem."
                action={
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setOnlyUnanswered(false);
                      setOrigem("");
                      setPage(1);
                    }}
                  >
                    Limpar filtros
                  </Button>
                }
              />
            ) : period.preset === "all" ? (
              <EmptyState
                icon={Users}
                title="Nenhum lead ainda."
                description="Eles chegam sozinhos pelos canais conectados (Meta, WhatsApp, landing page). Quem ligou ou apareceu na recepção entra pelo cadastro manual."
                action={<NewLeadDialog variant="outline" />}
              />
            ) : (
              <EmptyState
                icon={Users}
                title="Nenhum lead no período."
                description="Nada entrou entre as datas escolhidas. Troque o período no canto superior para ver mais."
              />
            )
          ) : (
            <>
              {/* Tabela a partir de md; abaixo disso a mesma lista vira cartões. */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full text-sm">
                  <thead className="bg-muted/60 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2.5">Lead</th>
                      <th className="px-4 py-2.5">Origem</th>
                      <th className="hidden px-4 py-2.5 xl:table-cell">Campanha</th>
                      <th className="px-4 py-2.5">Situação</th>
                      <th className="px-4 py-2.5">Responsável</th>
                      <th className="px-4 py-2.5">Entrou</th>
                      <th className="px-4 py-2.5">Resposta</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {rows.map((lead) => (
                      <tr
                        key={lead.id}
                        // A linha inteira abre a ficha; o nome continua um
                        // link de verdade para teclado e botão do meio.
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest("a, button")) return;
                          void abrir(lead.id);
                        }}
                        className="cursor-pointer transition-colors hover:bg-muted/40"
                      >
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            <Avatar
                              name={lead.contact.fullName}
                              seed={lead.contact.phoneNormalized ?? lead.id}
                              size="md"
                            />
                            <div className="min-w-0">
                              <Link
                                to="/leads/$leadId"
                                params={{ leadId: lead.id }}
                                className="block truncate font-semibold hover:text-primary"
                              >
                                {lead.contact.fullName ?? "Sem nome"}
                              </Link>
                              <p className="truncate text-xs tabular-nums text-muted-foreground">
                                {formatPhone(lead.contact.phoneNormalized)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <LeadSourceBadge source={lead.source} />
                        </td>
                        <td className="hidden max-w-48 truncate px-4 py-2.5 text-muted-foreground xl:table-cell">
                          {lead.campaignName ?? "—"}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex flex-wrap items-center gap-1">
                            <LeadStatusBadge status={lead.status} />
                            <LeadTemperatureBadge temperature={lead.temperature} />
                          </div>
                        </td>
                        <td className="max-w-40 truncate px-4 py-2.5 text-muted-foreground">
                          {lead.owner?.fullName ?? (
                            <span className="text-warning-foreground dark:text-warning">
                              sem responsável
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-muted-foreground">
                          {relativeTime(lead.createdAt)}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2.5 tabular-nums">
                          {lead.firstResponseAt ? (
                            <span className="text-muted-foreground">
                              {relativeTime(lead.firstResponseAt)}
                            </span>
                          ) : (
                            <span className="font-medium text-warning-foreground dark:text-warning">
                              pendente
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="divide-y md:hidden">
                {rows.map((lead) => (
                  <li key={lead.id}>
                    <Link
                      to="/leads/$leadId"
                      params={{ leadId: lead.id }}
                      className="flex gap-3 px-4 py-3 transition-colors hover:bg-muted/40 active:bg-muted/60"
                    >
                      <Avatar
                        name={lead.contact.fullName}
                        seed={lead.contact.phoneNormalized ?? lead.id}
                        size="md"
                        className="mt-0.5"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <p className="truncate text-sm font-semibold">
                            {lead.contact.fullName ?? "Sem nome"}
                          </p>
                          <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
                            {relativeTime(lead.createdAt)}
                          </span>
                        </div>
                        <p className="truncate text-xs tabular-nums text-muted-foreground">
                          {formatPhone(lead.contact.phoneNormalized)}
                        </p>
                        <div className="mt-1.5 flex flex-wrap items-center gap-1">
                          <LeadStatusBadge status={lead.status} />
                          <LeadTemperatureBadge temperature={lead.temperature} />
                          <LeadSourceBadge source={lead.source} />
                        </div>
                        <p className="mt-1.5 truncate text-xs text-muted-foreground">
                          {lead.owner?.fullName ?? (
                            <span className="text-warning-foreground dark:text-warning">
                              sem responsável
                            </span>
                          )}
                          <span className="mx-1 text-border">·</span>
                          {lead.firstResponseAt ? (
                            `respondido ${relativeTime(lead.firstResponseAt)}`
                          ) : (
                            <span className="font-medium text-warning-foreground dark:text-warning">
                              resposta pendente
                            </span>
                          )}
                        </p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            </>
          )}

          {pagination && pagination.totalPages > 1 && (
            <CardFooter className="justify-between">
              <span className="tabular-nums">
                Página {pagination.page} de {pagination.totalPages}
              </span>
              <div className="flex items-center gap-1">
                <Hint label="Página anterior">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label="Página anterior"
                    disabled={page <= 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <ChevronLeft />
                  </Button>
                </Hint>
                <Hint label="Próxima página">
                  <Button
                    variant="outline"
                    size="icon-sm"
                    aria-label="Próxima página"
                    disabled={page >= pagination.totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    <ChevronRight />
                  </Button>
                </Hint>
              </div>
            </CardFooter>
          )}
        </Card>
      </PageBody>
    </>
  );
}

/** Esqueleto com a altura de uma linha real: avatar, nome, telefone e etiquetas. */
function LinhasCarregando({ linhas = 8, className }: { linhas?: number; className?: string }) {
  return (
    <div className={cn("divide-y", className)} aria-busy>
      {Array.from({ length: linhas }, (_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <Skeleton className="size-9 rounded-full" />
          <div className="flex-1 space-y-1.5">
            <Skeleton className="h-3.5 w-40 max-w-[60%]" />
            <Skeleton className="h-3 w-24" />
          </div>
          <Skeleton className="hidden h-5 w-20 rounded-full md:block" />
          <Skeleton className="hidden h-5 w-16 rounded-full lg:block" />
        </div>
      ))}
    </div>
  );
}
